/**
 * Rental approval screen (§7.2, FR-4.3).
 *
 * Mobile-critical (§7.8): an owner approves from wherever they are, so this is a
 * single-column screen with two large controls and no horizontal layout.
 *
 * The approval copy is fixed (`ownerApprovalCopy`) and rendered verbatim — it is
 * the platform stating, at the moment of commitment, that it does not check
 * whether the owner was paid and cannot reverse the grant.
 */

import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ownerApprovalCopy } from '@pm/shared';

import { useApi, useMutation } from '../../hooks/useApi';
import type { Rental } from '../../api/types';
import { Card, Field, Loading, Badge } from '../../components/ui';
import { ErrorView, InlineError } from '../../components/ErrorView';
import { amountLabel, humanize, localDateTime } from '../../lib/format';
import { DECLARED_LABEL, RENTER_LABEL } from '../../lib/vocabulary';
import { routes } from '../../api/routes';

export function RentalApproval() {
  const { rentalId = '' } = useParams();
  const nav = useNavigate();
  const rental = useApi<Rental>(routes.rentals.one(rentalId));
  const approve = useMutation<Record<string, unknown>, Rental>(
    routes.rentals.approval(rentalId),
    { idempotent: true },
  );
  const decline = useMutation<Record<string, unknown>, Rental>(routes.rentals.decline(rentalId));
  const [reason, setReason] = useState('');

  if (rental.initialLoading) return <Loading label="Loading the request" />;
  if (rental.problem) return <ErrorView problem={rental.problem} onRetry={rental.reload} />;
  if (!rental.data) return null;

  const r = rental.data;
  const settled = r.status !== 'requested';

  return (
    <div className="narrow rental-approval">
      <header className="page-head">
        <h1>Rental request</h1>
        <p className="muted">
          {r.bot_name} · {RENTER_LABEL} @{r.renter_handle}
        </p>
      </header>

      <Card title="What they asked for">
        <p>
          {localDateTime(r.start_at)} to {localDateTime(r.end_at)}
        </p>
        <p>
          {DECLARED_LABEL}: <strong>{amountLabel(r.declared)}</strong>
        </p>
        <p className="muted">Request expires {localDateTime(r.request_expires_at)}.</p>
        <h3>Permissions they would hold</h3>
        <ul className="perm-chips">
          {r.granted_permissions.map((p) => (
            <li key={p} className="chip chip-static">
              {p.replace(/_/g, ' ')}
            </li>
          ))}
        </ul>
        <p className="muted">
          Included credits: {r.included_generation_credits.text} text,{' '}
          {r.included_generation_credits.image} image. Overage is covered by the{' '}
          {r.generation_overage_bearer}.
        </p>
      </Card>

      {settled ? (
        <Card>
          <p>
            This request is <Badge tone="neutral">{humanize(r.status)}</Badge>.
          </p>
          <button type="button" className="btn" onClick={() => nav('/bots')}>
            Back to My Bots
          </button>
        </Card>
      ) : (
        <Card title="Before you approve">
          {/* Verbatim FR-4.3 copy. */}
          <p className="approval-copy">{ownerApprovalCopy(localDateTime(r.end_at))}</p>

          {approve.problem ? <InlineError problem={approve.problem} /> : null}
          <button
            type="button"
            className="btn btn-primary btn-block"
            disabled={approve.pending}
            onClick={async () => {
              if ((await approve.run({})) !== null) nav(`/bots/${r.bot_id}/console`);
            }}
          >
            {approve.pending ? 'Approving…' : 'Approve and start the rental'}
          </button>

          <hr className="divider" />

          <Field id="ra-reason" label="Reason for declining" hint="Shared with the requester.">
            <textarea
              id="ra-reason"
              className="textarea"
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </Field>
          {decline.problem ? <InlineError problem={decline.problem} /> : null}
          <button
            type="button"
            className="btn btn-block"
            disabled={decline.pending}
            onClick={async () => {
              if ((await decline.run({ reason })) !== null) nav('/bots');
            }}
          >
            Decline
          </button>
        </Card>
      )}
    </div>
  );
}
